import {
  TEventSpecificData,
  TMableEvent,
} from '@/core/constants/event_types/base_mable_event';
import { TMableProduct } from '@/core/constants/event_types/extended_mable_event';

import {
  GAdsConversionPayload,
  GadsPayloadRetailItem,
} from '../google_ads_types';

import { EMableEventName } from '../../../../core/constants/event_types/events';

type PayloadParams = {
  mableEvent: TMableEvent;
};

export const GoogleAdsPayloadManager = {
  calculatePayload({ mableEvent }: PayloadParams): GAdsConversionPayload {
    const { bd, cdas, en: eventName, esd, pd, pids, sd } = mableEvent;
    const payload: any = {};

    // Standard conversion parameters
    const currency = this.getCurrency(esd);
    if (currency) {
      payload.currency = currency;
    }

    const value = this.getValue(eventName, esd);
    if (value) {
      payload.value = Number(value);
    }

    // Transaction details
    if (esd?.oid) {
      payload.transaction_id = esd.oid;
    }

    // Items array for retail events
    const items = this.getRetailItems(esd);
    if (items.length > 0) {
      payload.items = items;
    }

    // Page context
    if (pd) {
      if (pd.dl) payload.page_location = pd.dl;
      if (pd.dt) payload.page_title = pd.dt;
      if (pd.rl) payload.page_referrer = pd.rl;
    }

    // User context
    if (bd) {
      if (bd.ua) payload.user_agent = bd.ua;
      if (bd.ul) payload.user_language = bd.ul;
      if (bd.srw && bd.srh) {
        payload.screen_resolution = `${bd.srw}x${bd.srh}`;
      }
    }

    // Session data
    if (sd) {
      if (sd.sid) payload.session_id = sd.sid;
      if (sd.sct) payload.session_count = sd.sct;
      if (sd.ip4 || sd.ip6) {
        payload.ip_address = sd.ip4 || sd.ip6;
      }
    }

    // Enhanced ecommerce data
    if (esd?.cart) {
      // Cart-level data
      if (esd.cart.total) {
        const shipping = Number(esd.cart.total.shipping || 0);
        const tax = Number(esd.cart.total.tax || 0);
        const discount = Number(esd.cart.total.discount || 0);

        if (shipping > 0) payload.shipping = shipping;
        if (tax > 0) payload.tax = tax;
        if (discount > 0) payload.discount = discount;
      }

      // Payment information
      if (esd.pay_methods?.length) {
        payload.payment_type = esd.pay_methods[0];
      }

      // Cart items count
      if (esd.cart.quantity) {
        payload.num_items = esd.cart.quantity;
      }

      // Coupon information
      if (esd.cart.discounts?.length && esd.cart.discounts[0]?.code) {
        payload.coupon = esd.cart.discounts[0].code;
      }
    }

    if (esd?.cv_data) {
      const originalValue = Number(esd.cv_data.org_cv || 0);
      const discountAmount = Number(esd.cv_data.discount || 0);
      const shippingAmount = Number(esd.cv_data.shipping || 0);
      const taxAmount = Number(esd.cv_data.tax || 0);

      if (originalValue > 0) payload.original_value = originalValue;
      if (discountAmount > 0) payload.discount_amount = discountAmount;
      if (shippingAmount > 0) payload.shipping_amount = shippingAmount;
      if (taxAmount > 0) payload.tax_amount = taxAmount;
    }

    if (cdas) {
      if (cdas.street?.length) payload.user_address = cdas.street[0];
      if (cdas.zip?.length) payload.user_postal_code = cdas.zip[0];
      if (cdas.ct?.length) payload.user_city = cdas.ct[0];
      if (cdas.rg?.length) payload.user_region = cdas.rg[0];
      if (cdas.cc?.length) payload.user_country = cdas.cc[0];
    }

    // Campaign data
    if (pids) {
      if (pids.gclid?.length) payload.gclid = pids.gclid[0];
      if (pids.fbclid?.length) payload.fbclid = pids.fbclid[0];
      if (pids.ttclid?.length) payload.ttclid = pids.ttclid[0];
      if (pids.gacid?.length) payload.gacid = pids.gacid[0];
    }

    return payload;
  },

  createRetailItem(product: TMableProduct): GadsPayloadRetailItem {
    const retailItem: any = {
      google_business_vertical: 'retail',
    };

    if (product?.id) {
      retailItem.id = product.id;
    }

    const price = Number(product?.prc || 0);
    if (price > 0) {
      retailItem.price = price;
    }

    const quantity = product?.qty || 1;
    if (quantity > 0) {
      retailItem.quantity = quantity;
    }

    // Enhanced product details
    if (product?.nam) {
      retailItem.item_name = product.nam;
    }
    if (product?.br) {
      retailItem.item_brand = product.br;
    }
    if (product?.cat) {
      const categories = product.cat.split('/');
      if (categories[0]) retailItem.item_category = categories[0];
      if (categories[1]) retailItem.item_category2 = categories[1];
      if (categories[2]) retailItem.item_category3 = categories[2];
      if (categories[3]) retailItem.item_category4 = categories[3];
      if (categories[4]) retailItem.item_category5 = categories[4];
    }
    if (product?.var) {
      retailItem.item_variant = product.var;
    }

    // Product identifiers
    if (product?.ids) {
      if (product.ids.sku) retailItem.item_id = product.ids.sku;
      if (product.ids.pid) retailItem.product_id = product.ids.pid;
      if (product.ids.vid) retailItem.variant_id = product.ids.vid;
      if (product.ids.pnu) retailItem.product_number = product.ids.pnu;
    }

    // Discount information
    if (product?.totalDiscount) {
      const discount = Number(product.totalDiscount);
      if (discount > 0) {
        retailItem.discount = discount;
      }
    }

    return retailItem;
  },

  getCurrency(esd?: TEventSpecificData): string {
    return esd?.cart?.currency || esd?.act_product?.cur || '';
  },

  getRetailItems(esd?: TEventSpecificData): GadsPayloadRetailItem[] {
    if (!esd) return [];

    if (esd.cart?.products) {
      return esd.cart.products.map(this.createRetailItem);
    }

    return esd?.act_product ? [this.createRetailItem(esd.act_product)] : [];
  },

  getValue(
    eventName: EMableEventName,
    esd?: TEventSpecificData,
  ): string | undefined {
    // Highest priority: explicit conversion value
    if (esd?.cv_data?.cv !== undefined) {
      return esd.cv_data.cv.toString();
    }

    switch (eventName) {
      case EMableEventName.PRODUCT_ADDED: {
        return (
          Number(esd?.act_product?.prc || 0) * Number(esd?.qty_act_product || 1)
        ).toString();
      }

      case EMableEventName.PRODUCT_VIEWED:
      case EMableEventName.COLLECTION_VIEWED: {
        return esd?.act_product?.prc?.toString();
      }

      case EMableEventName.CHECKOUT_STARTED:
      case EMableEventName.PAYMENT_INFO_ADDED:
      case EMableEventName.ORDER_COMPLETED: {
        return esd?.cart?.total?.price?.toString();
      }

      case EMableEventName.LEAD: {
        return '0';
      } // Fixed lead value

      default: {
        return (
          esd?.cart?.total?.price?.toString() ||
          esd?.act_product?.prc?.toString()
        );
      }
    }
  },
};
